import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Tag, X, Check } from "lucide-react";
import { api, formatApiError, formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { STORE_NAME } from "@/lib/seo";

export default function Checkout() {
    const navigate = useNavigate();
    const { items, clear } = useCart();
    const { settings } = useSettings();
    const cur = settings?.currency_symbol || "Rs.";
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [form, setForm] = useState({
        full_name: "",
        phone: "",
        email: "",
        address_line1: "",
        address_line2: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
        notes: "",
    });

    // Promo code state
    const [codeInput, setCodeInput] = useState("");
    const [applied, setApplied] = useState(null); // { code, discount_amount, discount_type, discount_value }
    const [promoError, setPromoError] = useState("");
    const [applying, setApplying] = useState(false);
    const [suggested, setSuggested] = useState([]);

    // Server-computed shipping quote
    const [quote, setQuote] = useState(null);
    const [quoteLoading, setQuoteLoading] = useState(false);

    // Payment method
    const codEnabled = settings?.cod_enabled !== false; // visible while loading; hides only on explicit false
    const [paymentMethod, setPaymentMethod] = useState("cod"); // 'cod' | 'razorpay'

    // If admin disables COD while page is open (or settings load after mount with COD off),
    // auto-switch the selected method to Razorpay so the user is never stuck on a hidden option.
    useEffect(() => {
        if (!codEnabled && paymentMethod === "cod") {
            setPaymentMethod("razorpay");
        }
    }, [codEnabled, paymentMethod]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // Load suggested codes once
    useEffect(() => {
        api.get("/store/discounts/public").then((r) => setSuggested(r.data || [])).catch(() => {});
    }, []);

    const subtotal = items.reduce((s, it) => s + Number(it.price) * Number(it.quantity), 0);

    // Live shipping quote — re-fetch when cart contents OR applied code OR phone change.
    useEffect(() => {
        let cancelled = false;
        if (items.length === 0) return;
        setQuoteLoading(true);
        const payload = {
            items: items.map((it) => ({
                product_id: it.product_id,
                variant_id: it.variant_id || null,
                quantity: it.quantity,
            })),
            discount_code: applied?.code || undefined,
            phone: form.phone || undefined,
        };
        api.post("/store/shipping/quote", payload)
            .then((r) => {
                if (cancelled) return;
                setQuote(r.data);
                // If we had an applied code but server says it's no longer valid, reflect it.
                if (applied?.code && r.data?.discount_error) {
                    setApplied(null);
                    setPromoError(r.data.discount_error?.message || "Promo code is no longer valid.");
                } else if (applied?.code && r.data?.discount_amount != null) {
                    setApplied((prev) => prev && { ...prev, discount_amount: r.data.discount_amount });
                }
            })
            .catch(() => { if (!cancelled) setQuote(null); })
            .finally(() => { if (!cancelled) setQuoteLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        items.map((it) => `${it.product_id}:${it.variant_id || ""}:${it.quantity}`).join("|"),
        applied?.code,
        form.phone,
    ]);

    const shipping = Number(quote?.shipping_charge ?? 0);
    const discountAmount = applied ? Number(applied.discount_amount || 0) : 0;
    const subtotalAfterDiscount = Math.max(0, subtotal - discountAmount);
    const total = subtotalAfterDiscount + shipping;
    const usedFallback = !!quote?.shipping?.used_fallback_weight;

    // (Re-validation of applied codes happens via the live shipping/quote
    //  effect above — keeping discount + shipping in a single round-trip.)

    if (items.length === 0) {
        return (
            <div className="container mx-auto px-4 py-16 text-center">
                <SEO title={`Checkout | ${STORE_NAME}`} description="Secure checkout for your fishing tackle order." noindex />
                <h1 className="font-display text-3xl font-black uppercase">Cart empty</h1>
                <Link to="/shop" className="text-[#00BCE5] mt-3 inline-block">Browse the shop</Link>
            </div>
        );
    }

    const applyCode = async (codeStr) => {
        const code = (codeStr || codeInput || "").trim().toUpperCase();
        if (!code) {
            setPromoError("Please enter a promo code.");
            return;
        }
        setPromoError("");
        setApplying(true);
        try {
            const { data } = await api.post("/store/discounts/validate", {
                code,
                subtotal,
                phone: form.phone,
            });
            setApplied(data);
            setCodeInput(code);
            toast.success(`Code ${data.code} applied`);
        } catch (e) {
            const msg = e?.response?.data?.detail?.message || "This promo code is not valid.";
            setPromoError(msg);
            setApplied(null);
        } finally {
            setApplying(false);
        }
    };

    const removeCode = () => {
        setApplied(null);
        setCodeInput("");
        setPromoError("");
    };

    // Lazy-load the Razorpay Checkout.js script tag once.
    const loadRazorpayScript = () =>
        new Promise((resolve) => {
            if (window.Razorpay) return resolve(true);
            const existing = document.getElementById("razorpay-checkout-js");
            if (existing) {
                existing.addEventListener("load", () => resolve(true));
                existing.addEventListener("error", () => resolve(false));
                return;
            }
            const s = document.createElement("script");
            s.id = "razorpay-checkout-js";
            s.src = "https://checkout.razorpay.com/v1/checkout.js";
            s.onload = () => resolve(true);
            s.onerror = () => resolve(false);
            document.body.appendChild(s);
        });

    const validateForm = () => {
        const required = ["full_name", "phone", "address_line1", "city", "state", "pincode"];
        for (const r of required) {
            if (!form[r]?.trim()) {
                setError(`Please enter ${r.replace("_", " ")}`);
                return false;
            }
        }
        if (!/^\d{10}$/.test(form.phone.replace(/\D/g, "").slice(-10))) {
            setError("Please enter a valid 10-digit phone");
            return false;
        }
        return true;
    };

    const buildBasePayload = () => ({
        items: items.map((it) => ({
            product_id: it.product_id,
            variant_id: it.variant_id || null,
            quantity: it.quantity,
        })),
        shipping_address: {
            full_name: form.full_name,
            phone: form.phone,
            email: form.email,
            address_line1: form.address_line1,
            address_line2: form.address_line2,
            city: form.city,
            state: form.state,
            pincode: form.pincode,
            country: form.country,
        },
        notes: form.notes,
        discount_code: applied?.code || null,
    });

    const handleRazorpay = async () => {
        const ok = await loadRazorpayScript();
        if (!ok || !window.Razorpay) {
            setError("Could not load the secure payment window. Please retry.");
            return;
        }
        let orderResp;
        try {
            const { data } = await api.post("/store/payments/razorpay/order", buildBasePayload());
            orderResp = data;
        } catch (e) {
            const detail = e.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.message) {
                setError(detail.message);
            } else {
                setError(formatApiError(detail) || e.message);
            }
            return;
        }

        const rzp = new window.Razorpay({
            key: orderResp.key_id,
            amount: orderResp.amount,
            currency: orderResp.currency,
            name: orderResp.store_name || "Opensea Fishing Tackles",
            order_id: orderResp.razorpay_order_id,
            prefill: {
                name: form.full_name,
                contact: form.phone,
                email: form.email || "",
            },
            theme: { color: "#00BCE5" },
            modal: {
                ondismiss: () => {
                    setSubmitting(false);
                    setError("Payment was not completed. Please try again.");
                },
            },
            handler: async (rzpResponse) => {
                try {
                    const { data: order } = await api.post("/store/payments/razorpay/verify", {
                        razorpay_order_id: rzpResponse.razorpay_order_id,
                        razorpay_payment_id: rzpResponse.razorpay_payment_id,
                        razorpay_signature: rzpResponse.razorpay_signature,
                    });
                    clear();
                    toast.success("Payment successful!");
                    navigate(
                        `/order-success?n=${order.order_number}&p=${encodeURIComponent(form.phone)}`
                    );
                } catch (e) {
                    setError(
                        e?.response?.data?.detail ||
                            "Payment verification failed. If your card was charged, contact support."
                    );
                    setSubmitting(false);
                }
            },
        });
        rzp.on("payment.failed", () => {
            setError("Payment was not completed. Please try again.");
            setSubmitting(false);
        });
        rzp.open();
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!validateForm()) return;
        setSubmitting(true);
        if (paymentMethod === "razorpay") {
            await handleRazorpay();
            // submitting reset is handled inside handleRazorpay (success-navigates / failure-resets)
            return;
        }
        // COD path (unchanged)
        try {
            const payload = { ...buildBasePayload(), payment_method: "cod" };
            const { data } = await api.post("/store/checkout", payload);
            clear();
            toast.success("Order placed!");
            navigate(`/order-success?n=${data.order_number}&p=${encodeURIComponent(form.phone)}`);
        } catch (e) {
            const detail = e.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.message) {
                setError(detail.message);
                if (detail.code) setApplied(null);
            } else {
                setError(formatApiError(detail) || e.message);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const formatSuggested = (s) => {
        if (s.discount_type === "percent") {
            return `${s.discount_value}% off${s.max_discount_amount ? ` up to ${cur} ${s.max_discount_amount}` : ""}${s.min_order_amount ? ` on orders above ${cur} ${s.min_order_amount}` : ""}`;
        }
        return `${cur} ${s.discount_value} off${s.min_order_amount ? ` on orders above ${cur} ${s.min_order_amount}` : ""}`;
    };

    return (
        <div className="container mx-auto px-4 py-6 md:py-10" data-testid="checkout-page">
            <SEO title={`Checkout | ${STORE_NAME}`} description="Secure checkout for your fishing tackle order." noindex />
            <h1 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tight">Checkout</h1>

            <form onSubmit={submit} className="grid lg:grid-cols-[1fr,360px] gap-6 mt-6 md:mt-8">
                <div className="space-y-5">
                    <Section title="Shipping details">
                        <Grid2>
                            <Field label="Full name *">
                                <input data-testid="co-full-name" name="full_name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="ost-input" />
                            </Field>
                            <Field label="Phone *">
                                <input data-testid="co-phone" name="phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="ost-input" placeholder="10-digit mobile" />
                            </Field>
                        </Grid2>
                        <Field label="Email (for order updates)">
                            <input data-testid="co-email" name="email" value={form.email} onChange={(e) => set("email", e.target.value)} type="email" className="ost-input" />
                        </Field>
                        <Field label="Address line 1 *">
                            <input data-testid="co-addr1" name="address_line1" value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} className="ost-input" />
                        </Field>
                        <Field label="Address line 2">
                            <input data-testid="co-addr2" name="address_line2" value={form.address_line2} onChange={(e) => set("address_line2", e.target.value)} className="ost-input" />
                        </Field>
                        <Grid3>
                            <Field label="City *">
                                <input data-testid="co-city" name="city" value={form.city} onChange={(e) => set("city", e.target.value)} className="ost-input" />
                            </Field>
                            <Field label="State *">
                                <input data-testid="co-state" name="state" value={form.state} onChange={(e) => set("state", e.target.value)} className="ost-input" />
                            </Field>
                            <Field label="Pincode *">
                                <input data-testid="co-pincode" name="pincode" value={form.pincode} onChange={(e) => set("pincode", e.target.value)} className="ost-input" />
                            </Field>
                        </Grid3>
                        <Field label="Order notes (optional)">
                            <textarea data-testid="co-notes" name="notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="ost-input min-h-[80px]" />
                        </Field>
                    </Section>

                    <Section title="Payment method">
                        <div className="space-y-2" data-testid="payment-method-group">
                            {codEnabled && (
                                <label
                                    className={`flex items-start gap-3 p-4 border rounded-sm cursor-pointer transition-colors ${
                                        paymentMethod === "cod"
                                            ? "border-[#00BCE5] bg-[#00BCE5]/5"
                                            : "border-[#27272A] hover:border-white/30"
                                    }`}
                                    data-testid="pm-cod-label"
                                >
                                    <input
                                        type="radio"
                                        name="payment_method"
                                        value="cod"
                                        checked={paymentMethod === "cod"}
                                        onChange={() => setPaymentMethod("cod")}
                                        className="mt-1 accent-[#00BCE5]"
                                        data-testid="pm-cod"
                                    />
                                    <div>
                                        <div className="font-display uppercase font-bold tracking-widest text-white">Cash on Delivery</div>
                                        <div className="text-sm text-white/60 mt-1">Pay when your tackle arrives.</div>
                                    </div>
                                </label>
                            )}
                            <label
                                className={`flex items-start gap-3 p-4 border rounded-sm cursor-pointer transition-colors ${
                                    paymentMethod === "razorpay"
                                        ? "border-[#00BCE5] bg-[#00BCE5]/5"
                                        : "border-[#27272A] hover:border-white/30"
                                }`}
                                data-testid="pm-razorpay-label"
                            >
                                <input
                                    type="radio"
                                    name="payment_method"
                                    value="razorpay"
                                    checked={paymentMethod === "razorpay"}
                                    onChange={() => setPaymentMethod("razorpay")}
                                    className="mt-1 accent-[#00BCE5]"
                                    data-testid="pm-razorpay"
                                />
                                <div>
                                    <div className="font-display uppercase font-bold tracking-widest text-white">Pay online (Razorpay)</div>
                                    <div className="text-sm text-white/60 mt-1">UPI, cards, netbanking & wallets · secure popup.</div>
                                </div>
                            </label>
                        </div>
                    </Section>
                </div>

                <aside className="bg-[#121214] border border-[#27272A] rounded-sm p-5 h-fit lg:sticky lg:top-24" data-testid="checkout-summary">
                    <h3 className="font-display text-lg uppercase font-black mb-4">Summary</h3>
                    <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                        {items.map((it, idx) => (
                            <div key={idx} className="flex gap-3 text-sm">
                                <div className="w-12 h-12 bg-[#0A0A0B] border border-[#27272A] overflow-hidden flex-shrink-0">
                                    {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-white truncate">{it.name}</div>
                                    {it.variant_label && (
                                        <div className="text-[11px] uppercase tracking-widest text-white/55 font-display">
                                            {it.variant_label}
                                        </div>
                                    )}
                                    <div className="text-white/55 text-xs">× {it.quantity}</div>
                                </div>
                                <div className="text-white font-medium">{formatPrice(it.price * it.quantity, cur)}</div>
                            </div>
                        ))}
                    </div>

                    {/* Promo code block */}
                    <div className="border-t border-[#27272A] mt-4 pt-4" data-testid="promo-block">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-display text-white/65 mb-2">
                            <Tag size={12} /> Promo code
                        </div>
                        {applied ? (
                            <div className="flex items-center justify-between bg-[#00BCE5]/10 border border-[#00BCE5]/40 px-3 py-2 rounded-sm" data-testid="promo-applied">
                                <div className="flex items-center gap-2 text-sm">
                                    <Check size={14} className="text-[#34C759]" />
                                    <span className="font-display font-bold tracking-wider text-white">{applied.code}</span>
                                    <span className="text-white/60 text-xs">applied</span>
                                </div>
                                <button type="button" onClick={removeCode} className="text-white/55 hover:text-[#FF6E66]" aria-label="Remove code" data-testid="promo-remove">
                                    <X size={14} />
                                </button>
                            </div>
                        ) : (
                            <div className="flex gap-2">
                                <input
                                    value={codeInput}
                                    onChange={(e) => setCodeInput(e.target.value.toUpperCase().replace(/\s+/g, ""))}
                                    placeholder="Enter code"
                                    className="ost-input flex-1 uppercase"
                                    data-testid="promo-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => applyCode()}
                                    disabled={applying}
                                    className="border border-[#00BCE5] text-[#00BCE5] hover:bg-[#00BCE5]/10 px-3 rounded-sm font-display uppercase tracking-widest text-xs disabled:opacity-50"
                                    data-testid="promo-apply"
                                >
                                    {applying ? "…" : "Apply"}
                                </button>
                            </div>
                        )}
                        {promoError && (
                            <div className="text-xs text-[#FF6E66] mt-2" data-testid="promo-error">{promoError}</div>
                        )}

                        {!applied && suggested.length > 0 && (
                            <div className="mt-3 space-y-2" data-testid="promo-suggestions">
                                <div className="text-[10px] uppercase tracking-widest font-display text-white/45">Available codes</div>
                                {suggested.map((s) => (
                                    <div key={s.code} className="flex items-center justify-between bg-[#0A0A0B] border border-[#27272A] hover:border-[#00BCE5]/50 rounded-sm px-3 py-2 text-sm" data-testid={`promo-suggestion-${s.code}`}>
                                        <div className="min-w-0">
                                            <div className="font-display font-bold tracking-wider text-white">{s.code}</div>
                                            <div className="text-[11px] text-white/55 truncate">{s.description || formatSuggested(s)}</div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => applyCode(s.code)}
                                            className="text-[11px] uppercase tracking-widest font-display text-[#00BCE5] hover:underline ml-2"
                                            data-testid={`promo-use-${s.code}`}
                                        >
                                            Use code
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="border-t border-[#27272A] mt-4 pt-3 text-sm space-y-1.5">
                        <div className="flex justify-between"><span className="text-white/65">Subtotal</span><span data-testid="summary-subtotal">{formatPrice(subtotal, cur)}</span></div>
                        {applied && discountAmount > 0 && (
                            <div className="flex justify-between text-[#34C759]" data-testid="summary-discount">
                                <span>Discount ({applied.code})</span>
                                <span>− {formatPrice(discountAmount, cur)}</span>
                            </div>
                        )}
                        <div className="flex justify-between" data-testid="summary-shipping">
                            <span className="text-white/65">
                                Shipping {quoteLoading && <span className="text-white/35">…</span>}
                            </span>
                            <span>{shipping === 0 ? "FREE" : formatPrice(shipping, cur)}</span>
                        </div>
                    </div>
                    <p className="text-[10px] text-white/45 mt-1.5 leading-snug" data-testid="shipping-note">
                        {usedFallback
                            ? "Shipping calculated using standard estimate (some items missing weight)."
                            : "Shipping calculated based on product weight and package size."}
                    </p>
                    <div className="flex justify-between mt-3 pt-3 border-t border-[#27272A]">
                        <span className="font-display uppercase font-black">Total</span>
                        <span className="font-display font-black text-2xl" data-testid="summary-total">{formatPrice(total, cur)}</span>
                    </div>
                    {error && <div className="mt-3 text-sm text-[#FF3B30]" data-testid="co-error">{error}</div>}
                    <button
                        disabled={submitting}
                        type="submit"
                        className="w-full mt-5 bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest py-3.5 rounded-sm hover:bg-[#33C9EA] disabled:opacity-50 btn-primary-glow"
                        data-testid="co-submit"
                    >
                        {submitting
                            ? (paymentMethod === "razorpay" ? "Opening payment…" : "Placing order…")
                            : (paymentMethod === "razorpay" ? "Pay now" : "Place order")}
                    </button>
                </aside>
            </form>

            <style>{`
                .ost-input {
                    width: 100%;
                    background: #0A0A0B;
                    border: 1px solid #27272A;
                    border-radius: 4px;
                    padding: 12px 12px;
                    color: white;
                    outline: none;
                    transition: border-color 150ms ease;
                }
                .ost-input:focus { border-color: #00BCE5; }
            `}</style>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section className="bg-[#121214] border border-[#27272A] rounded-sm p-5">
            <h3 className="font-display text-lg uppercase font-black mb-4">{title}</h3>
            <div className="space-y-4">{children}</div>
        </section>
    );
}
function Field({ label, children }) {
    return (
        <div>
            <label className="block text-xs uppercase tracking-widest font-display font-bold text-white/65 mb-1.5">
                {label}
            </label>
            {children}
        </div>
    );
}
function Grid2({ children }) {
    return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}
function Grid3({ children }) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{children}</div>;
}
